// qr.js — a small, dependency-free QR Code encoder (byte mode, error
// correction level M, versions 1–40: up to 2331 bytes — sync links and shared programs).
// Follows ISO/IEC 18004 (structure after Project Nayuki's reference encoder).
//
//   qrMatrix(text) → boolean[][] (true = dark), or null when the text is too long
//   qrSvgPath(matrix, margin = 4) → { d, size }  one SVG path of all dark modules

// Per version (index = version): error-correction codewords per block and block count, level M.
// Versions 16–40 serve longer payloads (shared program links, up to 2331 bytes).
const ECC_PER_BLOCK = [0, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24,
  28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28];
const BLOCKS = [0, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10,
  10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49];
const MAX_VERSION = 40;

function rawModules(ver) {
  let r = (16 * ver + 128) * ver + 64;
  if (ver >= 2) {
    const na = Math.floor(ver / 7) + 2;
    r -= (25 * na - 10) * na - 55;
    if (ver >= 7) r -= 36;
  }
  return r;
}
const dataCodewords = (ver) => Math.floor(rawModules(ver) / 8) - ECC_PER_BLOCK[ver] * BLOCKS[ver];

// GF(256) arithmetic, polynomial x^8 + x^4 + x^3 + x^2 + 1.
function gfMul(x, y) {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
}
function rsDivisor(degree) {
  const r = new Array(degree).fill(0);
  r[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < r.length; j++) {
      r[j] = gfMul(r[j], root);
      if (j + 1 < r.length) r[j] ^= r[j + 1];
    }
    root = gfMul(root, 0x02);
  }
  return r;
}
function rsRemainder(data, divisor) {
  const r = divisor.map(() => 0);
  for (const b of data) {
    const factor = b ^ r.shift();
    r.push(0);
    for (let i = 0; i < divisor.length; i++) r[i] ^= gfMul(divisor[i], factor);
  }
  return r;
}

function alignmentPositions(ver, size) {
  if (ver === 1) return [];
  const na = Math.floor(ver / 7) + 2;
  const step = ver === 32 ? 26 : Math.ceil((ver * 4 + 4) / (na * 2 - 2)) * 2;
  const out = [6];
  for (let pos = size - 7; out.length < na; pos -= step) out.splice(1, 0, pos);
  return out;
}

const MASKS = [
  (x, y) => (x + y) % 2 === 0,
  (x, y) => y % 2 === 0,
  (x) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
];

// Finder-like 1:1:3:1:1 runs with 4 light modules on either side, as 11-bit words.
const FINDER_A = 0b10111010000, FINDER_B = 0b00001011101;
function penalty(m) {
  const n = m.length;
  let p = 0;
  for (let a = 0; a < n; a++) {
    let runR = 1, runC = 1, wR = 0, wC = 0;
    for (let b = 0; b < n; b++) {
      const r = m[a][b] ? 1 : 0, c = m[b][a] ? 1 : 0;
      if (b > 0) {
        if (m[a][b] === m[a][b - 1]) runR++; else { if (runR >= 5) p += runR - 2; runR = 1; }
        if (m[b][a] === m[b - 1][a]) runC++; else { if (runC >= 5) p += runC - 2; runC = 1; }
      }
      wR = ((wR << 1) | r) & 0x7ff; wC = ((wC << 1) | c) & 0x7ff;
      if (b >= 10) {
        if (wR === FINDER_A || wR === FINDER_B) p += 40;
        if (wC === FINDER_A || wC === FINDER_B) p += 40;
      }
    }
    if (runR >= 5) p += runR - 2;
    if (runC >= 5) p += runC - 2;
  }
  let dark = 0;
  for (let y = 0; y < n; y++) {
    const row = m[y], next = m[y + 1];
    for (let x = 0; x < n; x++) {
      if (row[x]) dark++;
      if (next && x < n - 1) { const c = row[x]; if (c === row[x + 1] && c === next[x] && c === next[x + 1]) p += 3; }
    }
  }
  const total = n * n;
  p += (Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1) * 10;
  return p;
}

const cache = new Map();
/** text → QR module matrix (rows of booleans, true = dark) or null if too long. Cached. */
export function qrMatrix(text) {
  const key = String(text);
  if (cache.has(key)) return cache.get(key);
  const m = encode(key);
  if (cache.size > 8) cache.clear();
  cache.set(key, m);
  return m;
}
function encode(text) {
  const data = Array.from(new TextEncoder().encode(String(text)));
  let ver = 1;
  for (; ver <= MAX_VERSION; ver++) if (4 + (ver < 10 ? 8 : 16) + data.length * 8 <= dataCodewords(ver) * 8) break;
  if (ver > MAX_VERSION) return null;

  // Data bits: byte mode, length, bytes, terminator, padding.
  const bits = [];
  const put = (val, len) => { for (let i = len - 1; i >= 0; i--) bits.push((val >>> i) & 1); };
  put(0b0100, 4);
  put(data.length, ver < 10 ? 8 : 16);
  for (const b of data) put(b, 8);
  const cap = dataCodewords(ver) * 8;
  put(0, Math.min(4, cap - bits.length));
  put(0, (8 - (bits.length % 8)) % 8);
  for (let pad = 0xec; bits.length < cap; pad ^= 0xec ^ 0x11) put(pad, 8);
  const cw = [];
  for (let i = 0; i < bits.length; i += 8) { let b = 0; for (let j = 0; j < 8; j++) b = (b << 1) | bits[i + j]; cw.push(b); }

  // Error correction per block, then interleave.
  const nb = BLOCKS[ver], eccLen = ECC_PER_BLOCK[ver], raw = Math.floor(rawModules(ver) / 8);
  const numShort = nb - (raw % nb), shortLen = Math.floor(raw / nb);
  const div = rsDivisor(eccLen);
  const blocks = [];
  for (let i = 0, k = 0; i < nb; i++) {
    const dat = cw.slice(k, k + shortLen - eccLen + (i < numShort ? 0 : 1));
    k += dat.length;
    const ecc = rsRemainder(dat, div);
    if (i < numShort) dat.push(0);
    blocks.push(dat.concat(ecc));
  }
  const all = [];
  for (let i = 0; i < blocks[0].length; i++) {
    for (let j = 0; j < blocks.length; j++) if (i !== shortLen - eccLen || j >= numShort) all.push(blocks[j][i]);
  }

  // Function patterns.
  const size = ver * 4 + 17;
  const mod = Array.from({ length: size }, () => new Array(size).fill(false));
  const fn = Array.from({ length: size }, () => new Array(size).fill(false));
  const setF = (x, y, dark) => { mod[y][x] = dark; fn[y][x] = true; };
  for (let i = 0; i < size; i++) { setF(6, i, i % 2 === 0); setF(i, 6, i % 2 === 0); }
  const finder = (cx, cy) => {
    for (let dy = -4; dy <= 4; dy++) for (let dx = -4; dx <= 4; dx++) {
      const x = cx + dx, y = cy + dy;
      if (x < 0 || y < 0 || x >= size || y >= size) continue;
      const d = Math.max(Math.abs(dx), Math.abs(dy));
      setF(x, y, d !== 2 && d !== 4);
    }
  };
  finder(3, 3); finder(size - 4, 3); finder(3, size - 4);
  const al = alignmentPositions(ver, size);
  for (let i = 0; i < al.length; i++) for (let j = 0; j < al.length; j++) {
    if ((i === 0 && j === 0) || (i === 0 && j === al.length - 1) || (i === al.length - 1 && j === 0)) continue;
    for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) setF(al[i] + dx, al[j] + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
  }
  const drawFormat = (mask) => {
    const d = (0 << 3) | mask; // level M = 0b00
    let rem = d;
    for (let k = 0; k < 10; k++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    const fb = ((d << 10) | rem) ^ 0x5412;
    const g = (k) => ((fb >>> k) & 1) === 1;
    for (let k = 0; k <= 5; k++) setF(8, k, g(k));
    setF(8, 7, g(6)); setF(8, 8, g(7)); setF(7, 8, g(8));
    for (let k = 9; k < 15; k++) setF(14 - k, 8, g(k));
    for (let k = 0; k < 8; k++) setF(size - 1 - k, 8, g(k));
    for (let k = 8; k < 15; k++) setF(8, size - 15 + k, g(k));
    setF(8, size - 8, true);
  };
  drawFormat(0); // reserve
  if (ver >= 7) {
    let rem = ver;
    for (let k = 0; k < 12; k++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    const vb = (ver << 12) | rem;
    for (let k = 0; k < 18; k++) {
      const bit = ((vb >>> k) & 1) === 1;
      const a = size - 11 + (k % 3), b = Math.floor(k / 3);
      setF(a, b, bit); setF(b, a, bit);
    }
  }

  // Data, in the zig-zag order.
  let i = 0;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const up = ((right + 1) & 2) === 0;
        const y = up ? size - 1 - vert : vert;
        if (!fn[y][x] && i < all.length * 8) {
          mod[y][x] = ((all[i >>> 3] >>> (7 - (i & 7))) & 1) === 1;
          i++;
        }
      }
    }
  }

  // Pick the mask with the lowest penalty.
  const base = mod.map((r) => r.slice());
  let best = null, bestP = Infinity, bestMask = 0;
  for (let mask = 0; mask < 8; mask++) {
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) mod[y][x] = fn[y][x] ? mod[y][x] : (base[y][x] !== MASKS[mask](x, y));
    drawFormat(mask);
    const p = penalty(mod);
    if (p < bestP) { bestP = p; best = mod.map((r) => r.slice()); bestMask = mask; }
  }
  void bestMask;
  return best;
}

/** One SVG path for the dark modules, offset by a quiet-zone margin. */
export function qrSvgPath(matrix, margin = 4) {
  if (!matrix) return null;
  let d = '';
  matrix.forEach((row, y) => row.forEach((dark, x) => { if (dark) d += `M${x + margin} ${y + margin}h1v1h-1z`; }));
  return { d, size: matrix.length + margin * 2 };
}
